import React, { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { FiArrowLeft, FiCalendar, FiEdit2, FiFileText, FiPlus, FiUpload } from 'react-icons/fi';
import api from '../services/api';
import DocumentosSection from '../components/DocumentosSection';
import { TiposProcessoOptions } from '../constants/tiposProcessoLicenciamento';
import './ProcessoLicenciamentoDetalhes.css';

const STATUS = [
  ['preparacao', 'Preparação'],
  ['protocolado', 'Protocolado'],
  ['em_analise', 'Em análise'],
  ['pendencia', 'Pendência'],
  ['concluido', 'Concluído'],
  ['cancelado', 'Cancelado']
];

const vazio = {
  titulo: '', tipo: 'outro', orgao_ambiental: '', numero_processo: '',
  descricao: '', data_abertura: '', data_limite: '', status: 'preparacao'
};

const dataBr = (valor) => valor ? new Date(`${String(valor).slice(0, 10)}T12:00:00`).toLocaleDateString('pt-BR') : '-';

export default function ProcessoLicenciamentoDetalhes() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [dados, setDados] = useState(null);
  const [erro, setErro] = useState('');
  const [editando, setEditando] = useState(false);
  const [form, setForm] = useState(vazio);
  const [prazo, setPrazo] = useState({ titulo: '', descricao: '', data_vencimento: '', prioridade: 'media' });
  const [pdf, setPdf] = useState(null);
  const [enviando, setEnviando] = useState(false);

  const carregar = useCallback(async () => {
    try {
      const res = await api.get(`/api/processos-licenciamento/${id}`);
      setDados(res.data);
      setForm({ ...vazio, ...res.data.processo });
      setErro('');
    } catch (err) {
      setErro(err.response?.data?.error || 'Erro ao carregar processo de licenciamento');
    }
  }, [id]);

  useEffect(() => { carregar(); }, [carregar]);

  const salvar = async (e) => {
    e.preventDefault();
    try {
      await api.put(`/api/processos-licenciamento/${id}`, form);
      setEditando(false);
      carregar();
    } catch (err) {
      alert(err.response?.data?.error || 'Erro ao atualizar processo');
    }
  };

  const adicionarPrazo = async (e) => {
    e.preventDefault();
    try {
      await api.post('/api/tarefas', { ...prazo, processo_licenciamento_id: id });
      setPrazo({ titulo: '', descricao: '', data_vencimento: '', prioridade: 'media' });
      carregar();
    } catch (err) {
      alert(err.response?.data?.error || 'Erro ao criar prazo');
    }
  };

  const vincularLicenca = async (e) => {
    e.preventDefault();
    if (!pdf) return;
    setEnviando(true);
    try {
      const fd = new FormData();
      fd.append('pdf', pdf);
      const res = await api.post(`/api/processos-licenciamento/${id}/vincular-licenca`, fd);
      navigate(res.data?.licenca?.redirect || `/licencas/${res.data?.licenca?.id}/validar`);
    } catch (err) {
      alert(err.response?.data?.error || 'Erro ao vincular licença emitida');
    } finally {
      setEnviando(false);
    }
  };

  if (erro) return <div className="processo-page"><p>{erro}</p><button className="btn btn-secondary" onClick={() => navigate(-1)}>Voltar</button></div>;
  if (!dados) return <div className="processo-page">Carregando processo...</div>;
  const { processo, documentos = [], tarefas = [], historico = [] } = dados;
  const exigeProtocolo = form.status !== 'preparacao';

  return (
    <div className="processo-page">
      <header className="processo-header">
        <div>
          <Link to={`/empreendimentos/${processo.empreendimento_id}`} className="processo-back"><FiArrowLeft /> {processo.empreendimento_nome}</Link>
          <h1>{processo.titulo}</h1>
          <span className={`processo-status status-${processo.status}`}>{STATUS.find(([v]) => v === processo.status)?.[1] || processo.status}</span>
        </div>
        <button className="btn btn-secondary" onClick={() => setEditando(true)}><FiEdit2 /> Editar</button>
      </header>

      <section className="processo-card processo-grid">
        <p><strong>Tipo:</strong> {processo.tipo || '-'}</p>
        <p><strong>Órgão ambiental:</strong> {processo.orgao_ambiental || '-'}</p>
        <p><strong>Número:</strong> {processo.numero_processo || 'Ainda não informado'}</p>
        <p><strong>Abertura:</strong> {dataBr(processo.data_abertura)}</p>
        <p><strong>Prazo:</strong> {dataBr(processo.data_limite)}</p>
        <p><strong>Descrição:</strong> {processo.descricao || '-'}</p>
      </section>

      {processo.status !== 'concluido' && (
        <section className="processo-card">
          <h2><FiUpload /> Vincular licença emitida</h2>
          <p>Envie o PDF da licença. O sistema seguirá o fluxo normal de extração e manterá os documentos deste processo vinculados à nova licença.</p>
          <form className="processo-inline-form" onSubmit={vincularLicenca}>
            <input type="file" accept="application/pdf" required onChange={(e) => setPdf(e.target.files?.[0] || null)} />
            <button className="btn btn-primary" disabled={enviando}>{enviando ? 'Enviando...' : 'Vincular licença emitida'}</button>
          </form>
        </section>
      )}

      <section className="processo-card">
        <h2><FiCalendar /> Prazos administrativos</h2>
        <form className="processo-prazo-form" onSubmit={adicionarPrazo}>
          <input required placeholder="Título do prazo" value={prazo.titulo} onChange={(e) => setPrazo({ ...prazo, titulo: e.target.value })} />
          <input type="date" value={prazo.data_vencimento} onChange={(e) => setPrazo({ ...prazo, data_vencimento: e.target.value })} />
          <select value={prazo.prioridade} onChange={(e) => setPrazo({ ...prazo, prioridade: e.target.value })}>
            <option value="baixa">Baixa</option><option value="media">Média</option><option value="alta">Alta</option>
          </select>
          <button className="btn btn-primary"><FiPlus /> Adicionar prazo</button>
        </form>
        {tarefas.length === 0 ? <p className="text-muted">Nenhum prazo cadastrado.</p> : (
          <div className="processo-lista">{tarefas.map((t) => (
            <div className="processo-lista-item" key={t.id}>
              <strong>{t.titulo}</strong><span>{dataBr(t.data_vencimento)} · {t.status}</span>
              <Link to={`/tarefas/kanban?tarefa=${t.id}`}>Abrir no Kanban</Link>
            </div>
          ))}</div>
        )}
      </section>

      <section className="processo-card">
        <h2><FiFileText /> Documentos do processo</h2>
        <DocumentosSection processoLicenciamentoId={id} empreendimentoId={processo.empreendimento_id} documentos={documentos} onRefresh={carregar} onDocumentAdded={carregar} />
      </section>

      <section className="processo-card">
        <h2>Histórico</h2>
        {historico.length === 0 ? <p className="text-muted">Nenhum registro.</p> : (
          <div className="processo-lista">{historico.map((h) => (
            <div className="processo-lista-item" key={h.id}><strong>{h.descricao}</strong><span>{new Date(h.criado_em).toLocaleString('pt-BR')} · {h.usuario_nome || 'Sistema'}</span></div>
          ))}</div>
        )}
      </section>

      {editando && (
        <div className="modal-overlay" onClick={() => setEditando(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h2>Editar processo</h2>
            <form onSubmit={salvar}>
              <label>Título *<input required value={form.titulo} onChange={(e) => setForm({ ...form, titulo: e.target.value })} /></label>
              <label>Tipo<select value={form.tipo} onChange={(e) => setForm({ ...form, tipo: e.target.value })}>{TiposProcessoOptions()}</select></label>
              <label>Órgão ambiental<input value={form.orgao_ambiental || ''} onChange={(e) => setForm({ ...form, orgao_ambiental: e.target.value })} /></label>
              <label>Número do processo {exigeProtocolo && '*'}<input required={exigeProtocolo} value={form.numero_processo || ''} onChange={(e) => setForm({ ...form, numero_processo: e.target.value })} /></label>
              <label>Data de abertura {exigeProtocolo && '*'}<input type="date" required={exigeProtocolo} value={form.data_abertura || ''} onChange={(e) => setForm({ ...form, data_abertura: e.target.value })} /></label>
              <label>Prazo<input type="date" value={form.data_limite || ''} onChange={(e) => setForm({ ...form, data_limite: e.target.value })} /></label>
              <label>Status<select value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })}>{STATUS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}</select></label>
              <label>Descrição<textarea rows="3" value={form.descricao || ''} onChange={(e) => setForm({ ...form, descricao: e.target.value })} /></label>
              <div className="form-actions"><button type="button" className="btn btn-secondary" onClick={() => setEditando(false)}>Cancelar</button><button className="btn btn-primary">Salvar</button></div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
